/**
 * Demo spend for ?mock=1: derived from the mock sessions so the Spend page, the Sessions
 * cost chips and Overview's budget rows agree with everything else on screen. Budgets made
 * in demo mode live only in this tab.
 */
import type {
  AccountBucket,
  Budget,
  BudgetInput,
  BudgetStatus,
  CostBucket,
  CostsState,
  Id,
  ModelPrice,
  SessionUsage,
  SpendAccount,
  TokenCounts,
  UsageTotals,
} from '@orgus/protocol';
import { UNKNOWN_ACCOUNT_LABEL } from '@orgus/protocol';
import { newPrefixedId } from '@orgus/protocol';
import { create } from 'zustand';
import { MOCK_IDS } from '../../lib/mock.ts';
import type { Entities } from '../../lib/store.ts';
import { modelLabel } from '../sessions/labels.ts';

/** Family list prices (USD per million tokens) for demo numbers only; the hub owns real pricing. */
const RATES: Record<string, { input: number; output: number; cacheRead: number }> = {
  opus: { input: 4, output: 20, cacheRead: 0.2 },
  sonnet: { input: 2, output: 10, cacheRead: 0.1 },
  haiku: { input: 0.1, output: 0.5, cacheRead: 0.01 },
};

const DAY = 86_400_000;

const rates = (input: number, output: number, cacheRead: number) => ({ input, output, cacheRead, cacheWrite: input * 1.25, cacheWrite1h: input * 2 });
const DEMO_PRICES: ModelPrice[] = [
  { match: 'claude-fable-5-1', label: 'Fable 5.1', ...rates(10, 50, 0.25) },
  { match: 'claude-opus-5-5', label: 'Opus 5.5', ...rates(4, 20, 0.2) },
  { match: 'claude-opus-5', label: 'Opus 5', ...rates(5, 25, 0.5) },
  { match: 'claude-sonnet-5-5', label: 'Sonnet 5.5', ...rates(2, 10, 0.1) },
  { match: 'claude-sonnet-5', label: 'Sonnet 5', ...rates(2, 10, 0.2) },
  { match: 'claude-haiku-5-5', label: 'Haiku 5.5', ...rates(0.1, 0.5, 0.01) },
];

function familyOf(model: string | null): keyof typeof RATES {
  const m = /opus|sonnet|haiku/.exec(model ?? '')?.[0] as keyof typeof RATES | undefined;
  return m ?? 'opus';
}

interface MockCostsStore {
  budgets: Budget[];
  billing: 'subscription' | 'api';
  /** Demo renames of accounts, by key. */
  names: Record<string, string>;
  upsert(input: BudgetInput, id?: Id): Budget;
  remove(id: Id): void;
  setBilling(b: 'subscription' | 'api'): void;
  rename(key: string, name: string): void;
}

/**
 * Demo accounts (ADR 0067): three Anthropic organisations (one known from sign-in history), an
 * Azure Foundry resource, and an API key the provider report names (amended 19 Oct 2026).
 */
const MOCK_ACCOUNTS: Omit<SpendAccount, 'name' | 'firstSeen' | 'lastSeen'>[] = [
  { key: 'anthropic:7c1d2e3f-4a5b-4c6d-8e7f-9a0b1c2d3e4f', kind: 'anthropic', label: 'Acme Labs', billing: 'subscription', source: 'config' },
  { key: 'foundry:acme-ai-east', kind: 'foundry', label: 'Foundry acme-ai-east', billing: 'usage', source: 'launch-env' },
  { key: 'anthropic:0f1e2d3c-4b5a-4968-8778-695a4b3c2d1e', kind: 'anthropic', label: 'Claude Max 20x', billing: 'subscription', source: 'credential-org' },
  { key: 'anthropic:4a6c2e0b-1d3f-4e5a-9b7c-8d0e2f4a6c1b', kind: 'anthropic', label: 'Globex Enterprise', billing: 'usage', source: 'history' },
  { key: 'api-key:apikey_01HqZkM3pT8vW2xY4nB6cDeF', kind: 'api-key', label: 'Developer key', billing: 'usage', source: 'provider-report' },
];
const MOCK_HISTORY = MOCK_ACCOUNTS[3]!.key;
const MOCK_API_KEY = MOCK_ACCOUNTS[4]!.key;
/** The demo report's API-key usage: this share of the history account's spend moves to the key, plus usage no session holds. */
const MOCK_CARVE = 0.42;
const MOCK_UNMATCHED_SHARE = 0.08;
/** Which demo account a session ran on: the team plan, Foundry, a personal Max, older ones from sign-in history, one unknown. */
function mockAccountOf(id: string): string {
  const n = noise(`acct${id}`);
  return n < 0.3 ? MOCK_ACCOUNTS[0]!.key : n < 0.5 ? MOCK_ACCOUNTS[1]!.key : n < 0.75 ? MOCK_ACCOUNTS[2]!.key : n < 0.97 ? MOCK_HISTORY : '';
}

const seeded = (now: number): Budget[] => [
  { id: 'bud_demo_org', scope: { kind: 'org' }, period: 'month', limit: 600, softAt: 0.8, onLimit: 'warn', createdAt: now - 20 * DAY, updatedAt: now - 20 * DAY },
  { id: 'bud_demo_billing', scope: { kind: 'project', projectId: MOCK_IDS.billing }, period: 'month', limit: 160, softAt: 0.8, onLimit: 'stop', createdAt: now - 9 * DAY, updatedAt: now - 9 * DAY },
  { id: 'bud_demo_day', scope: { kind: 'org' }, period: 'day', limit: 40, softAt: 0.8, onLimit: 'warn', createdAt: now - 3 * DAY, updatedAt: now - 3 * DAY },
];

export const useMockCosts = create<MockCostsStore>()((set, get) => ({
  budgets: seeded(Date.now()),
  billing: 'subscription',
  names: {},
  upsert(input, id) {
    const now = Date.now();
    const prev = id ? get().budgets.find((b) => b.id === id) : undefined;
    const b: Budget = { softAt: 0.8, onLimit: 'warn', ...input, id: prev?.id ?? newPrefixedId('bud', now), createdAt: prev?.createdAt ?? now, updatedAt: now } as Budget;
    set((s) => ({ budgets: prev ? s.budgets.map((x) => (x.id === b.id ? b : x)) : [...s.budgets, b] }));
    return b;
  },
  remove(id) {
    set((s) => ({ budgets: s.budgets.filter((b) => b.id !== id) }));
  },
  setBilling(billing) {
    set({ billing });
  },
  rename(key, name) {
    set((s) => {
      const names = { ...s.names };
      if (name) names[key] = name;
      else delete names[key];
      return { names };
    });
  },
}));

function totals(t: TokenCounts, cost: number): UsageTotals {
  return { input: t.input, output: t.output, cacheRead: t.cacheRead, cacheWrite: t.cacheWrite, cost };
}

/** A deterministic 0..1 from a string, so demo history does not change between renders. */
function noise(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return ((h >>> 0) % 1000) / 1000;
}

const pad = (n: number) => String(n).padStart(2, '0');
const dayKey = (ts: number) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function buildMockCosts(e: Pick<Entities, 'sessions' | 'projects' | 'machines'>, now: number, budgets: Budget[], billing: 'subscription' | 'api', names: Record<string, string> = {}): CostsState {
  const d = new Date(now);
  const monthStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
  const sessions: SessionUsage[] = [];
  const project = new Map<string, CostBucket>();
  const machine = new Map<string, CostBucket>();
  const model = new Map<string, CostBucket>();
  const account = new Map<string, CostBucket>();
  const accounts: SpendAccount[] = MOCK_ACCOUNTS.map((a, i) => ({ ...a, name: names[a.key] ?? null, firstSeen: now - (40 + i * 9) * DAY, lastSeen: now - i * 3_600_000 }));
  const accountName = (key: string) => {
    const a = accounts.find((x) => x.key === key);
    return a ? (a.name ?? a.label) : UNKNOWN_ACCOUNT_LABEL;
  };
  const tokensMonth: TokenCounts = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, cacheWrite1h: 0, calls: 0 };
  const bump = (m: Map<string, CostBucket>, key: string, label: string, cost: number, tokens: number) => {
    const b = m.get(key) ?? { key, label, cost: 0, tokens: 0, sessions: 0 };
    b.cost += cost;
    b.tokens += tokens;
    b.sessions += 1;
    m.set(key, b);
  };

  for (const s of Object.values(e.sessions)) {
    const fam = familyOf(s.model);
    const r = RATES[fam]!;
    // The demo sessions report modest counts; scale them so a month looks like a real team's.
    const k = 40;
    const write = Math.round(s.tokens.input * k * 0.3);
    const t: TokenCounts = { input: Math.round(s.tokens.input * k * 0.7), output: s.tokens.output * k, cacheRead: s.tokens.cacheRead * k, cacheWrite: write, cacheWrite1h: 0, calls: s.turns * 6 };
    const cost = (t.input * r.input + t.output * r.output + t.cacheRead * r.cacheRead + t.cacheWrite * r.input * 1.25) / 1e6;
    const key = s.model ?? 'claude-opus-5';
    const tok = t.input + t.output + t.cacheRead + t.cacheWrite;
    const rate = s.status === 'working' ? Math.round(tok / 45) : 0;
    const accountKey = mockAccountOf(s.id);
    const acctBilling = accounts.find((a) => a.key === accountKey)?.billing;
    sessions.push({
      sessionId: s.id,
      machineId: s.machineId,
      projectId: s.projectId,
      missionId: null,
      title: s.title,
      byModel: { [key]: totals(t, cost) },
      total: totals(t, cost),
      source: acctBilling === 'usage' ? 'transcript' : acctBilling === 'subscription' || billing !== 'api' ? 'notional' : 'transcript',
      pricingVersion: '2026-10-16',
      rate,
      partial: false,
      accountKey: accountKey || null,
      updatedAt: s.lastActivity,
    });
    bump(account, accountKey, accountName(accountKey), cost, tok);
    bump(project, s.projectId ?? '', s.projectId ? (e.projects[s.projectId]?.name ?? s.projectId) : 'No project', cost, tok);
    bump(machine, s.machineId, e.machines[s.machineId]?.name ?? s.machineId, cost, tok);
    bump(model, key, modelLabel(key), cost, tok);
    for (const f of ['input', 'output', 'cacheRead', 'cacheWrite', 'calls'] as const) tokensMonth[f] += t[f];
  }
  sessions.sort((a, b) => b.total.cost - a.total.cost);
  const live = sessions.reduce((n, s) => n + s.total.cost, 0);

  // Earlier days of the month: a steady weekday rhythm with quieter weekends.
  const byDay: CostBucket[] = [];
  let earlier = 0;
  for (let i = 29; i >= 0; i--) {
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate() - i, 12).getTime();
    const key = dayKey(day);
    const weekend = [0, 6].includes(new Date(day).getDay());
    // Today: a busy day, near the demo daily budget so its warning shows.
    const cost = i === 0 ? 33 + noise(key) * 6 : (weekend ? 4 : 14) + noise(key) * (weekend ? 6 : 16);
    if (i > 0 && day >= monthStart) earlier += cost;
    byDay.push({ key, label: key, cost, tokens: Math.round(cost * 260_000), sessions: Math.round(cost / 2) + 1 });
  }
  const month = earlier + byDay[byDay.length - 1]!.cost;
  const scale = month / Math.max(live, 1e-9);
  // Sessions carry the same scale as the month, so a session, a mission and the month agree
  // (a mission's spend is the sum of its sessions; it can never exceed the month).
  const scaleTotals = (u: UsageTotals): UsageTotals => ({
    input: Math.round(u.input * scale),
    output: Math.round(u.output * scale),
    cacheRead: Math.round(u.cacheRead * scale),
    cacheWrite: Math.round(u.cacheWrite * scale),
    cost: u.cost * scale,
  });
  for (const u of sessions) {
    u.total = scaleTotals(u.total);
    u.byModel = Object.fromEntries(Object.entries(u.byModel).map(([k, v]) => [k, scaleTotals(v)]));
    u.rate = Math.round(u.rate * scale);
  }
  const scaled = (m: Map<string, CostBucket>) =>
    [...m.values()].map((b) => ({ ...b, cost: b.cost * scale, tokens: Math.round(b.tokens * scale) })).sort((a, b) => b.cost - a.cost);
  const byProject = scaled(project);
  // The provider report's API key, carved out of the history account as the hub does (ADR 0067, amended 19 Oct 2026).
  const history = account.get(MOCK_HISTORY);
  if (history) {
    const moved = history.cost * MOCK_CARVE;
    account.set(MOCK_API_KEY, { key: MOCK_API_KEY, label: accountName(MOCK_API_KEY), cost: moved, tokens: Math.round(history.tokens * MOCK_CARVE), sessions: history.sessions });
    account.set(MOCK_HISTORY, { ...history, cost: history.cost - moved, tokens: Math.round(history.tokens * (1 - MOCK_CARVE)) });
  }
  const byAccount: AccountBucket[] = scaled(account).map((b) => {
    const a = accounts.find((x) => x.key === b.key);
    const carved = history ? history.cost * MOCK_CARVE * scale : 0;
    return {
      ...b,
      kind: a?.kind ?? null,
      billing: a?.billing ?? 'unknown',
      ...(a ? { source: a.source } : {}),
      ...(b.key === MOCK_HISTORY && carved ? { movedOut: carved } : {}),
      ...(b.key === MOCK_API_KEY && carved ? { unmatched: (carved / MOCK_CARVE) * MOCK_UNMATCHED_SHARE } : {}),
    };
  });
  const today = byDay[byDay.length - 1]!.cost;
  const elapsed = Math.max(DAY, now - monthStart);
  const forecastMonth = month + (month / elapsed) * (monthEnd - now);

  const budgetStatus: BudgetStatus[] = budgets.map((b) => {
    const spent =
      b.period === 'day'
        ? b.scope.kind === 'org'
          ? today
          : 0
        : b.scope.kind === 'org'
          ? month
          : b.scope.kind === 'project'
            ? (byProject.find((p) => p.key === (b.scope as { projectId: string }).projectId)?.cost ?? 0)
            : b.scope.kind === 'session'
              ? (sessions.find((s) => s.sessionId === (b.scope as { sessionId: string }).sessionId)?.total.cost ?? 0)
              : 0;
    const end = b.period === 'day' ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() : b.period === 'month' ? monthEnd : null;
    const start = b.period === 'day' ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() : b.period === 'month' ? monthStart : 0;
    const fc = end === null ? spent : spent + (spent / Math.max(b.period === 'day' ? 3600_000 : DAY, now - start)) * (end - now);
    const name = b.scope.kind === 'org' ? 'Everything' : b.scope.kind === 'project' ? (e.projects[b.scope.projectId]?.name ?? b.scope.projectId) : b.scope.kind === 'session' ? (e.sessions[b.scope.sessionId]?.title ?? 'Session') : 'Mission';
    const word = b.period === 'day' ? 'today' : b.period === 'month' ? 'this month' : 'in total';
    return {
      budgetId: b.id,
      label: `${name} ${word}`,
      spent,
      limit: b.limit,
      forecast: fc,
      state: spent >= b.limit ? 'hard' : spent >= b.softAt * b.limit ? 'soft' : 'ok',
      forecastOver: fc >= b.limit,
      periodStart: start,
      periodEnd: end,
    };
  });

  const hot = sessions.find((s) => s.rate > 0);
  return {
    pricing: { version: '2026-10-16', effectiveFrom: Date.UTC(2026, 9, 16), source: 'https://platform.claude.com/docs/en/about-claude/pricing', prices: DEMO_PRICES },
    billing,
    sessions,
    budgets,
    budgetStatus,
    today,
    month,
    forecastMonth,
    byProject,
    byMachine: scaled(machine),
    byModel: scaled(model),
    byMission: [],
    byAccount,
    accounts,
    apiKeys: { reportConnected: true, apiKeyApproved: true },
    byDay,
    tokensMonth: { ...tokensMonth, input: Math.round(tokensMonth.input * scale), output: Math.round(tokensMonth.output * scale), cacheRead: Math.round(tokensMonth.cacheRead * scale), cacheWrite: Math.round(tokensMonth.cacheWrite * scale) },
    runaways: hot
      ? [{ sessionId: hot.sessionId, machineId: hot.machineId, projectId: hot.projectId, title: hot.title, rate: hot.rate * 6, baseline: hot.rate, at: now - 4 * 60_000, active: true }]
      : [],
    unpricedModels: [],
    monthStart,
    monthEnd,
    updatedAt: now,
  };
}
